import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MapPin, Trash2, Plus } from 'lucide-react';

const LOCATION_TYPES = ['ABA Center', 'Daycare/School', 'School', 'Montessori', 'Home', 'Other'];

// Commits text edits on blur / Enter so we don't spam PATCH on every
// keystroke and so the input keeps focus while the user types.
function InlineTextInput({ value, onCommit, ...rest }) {
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);
  const commit = () => {
    if ((draft || '') !== (value || '')) onCommit(draft);
  };
  return (
    <Input
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
      {...rest}
    />
  );
}

/**
 * Outside service locations — table + inline add form.
 * Everything is bubbled to the parent via handlers so this component is stateless
 * except for the "new location draft" it owns.
 */
export default function OutsideLocationsPanel({
  clinic, canEdit,
  newLoc, setNewLoc,
  addLocation, updateLocation, removeLocation,
}) {
  const accepting = (loc) => loc.accepting_status || (loc.accepting_new ? 'Yes' : 'No');
  const acceptingClass = (val) =>
    val === 'Yes' ? 'bg-[#839E97]/20 text-[#839E97] border-[#839E97]/40'
      : val === 'No' ? 'bg-[#8A3A3A]/15 text-[#8A3A3A] border-[#8A3A3A]/40'
      : 'bg-[#B08A3A]/20 text-[#B08A3A] border-[#B08A3A]/40';

  return (
    <Card className="rounded-2xl border-[#E8E5EC] p-6" data-testid="outside-locations">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h3 className="font-serif text-xl text-[#2D2833] flex items-center gap-2"><MapPin size={18} /> Outside service locations</h3>
          <div className="text-xs text-[#685B72] mt-0.5">Serviced by {clinic.name} · used for eval/therapy location dropdowns when creating clients</div>
        </div>
        <span className="text-xs text-[#685B72]">{(clinic.outside_locations || []).length} locations</span>
      </div>

      {canEdit && (
        <div className="grid grid-cols-1 md:grid-cols-8 gap-2 mb-4 p-3 rounded-xl bg-[#FAF8FB] border border-[#E8E5EC]">
          <Input placeholder="Location name" value={newLoc.name} onChange={e => setNewLoc({ ...newLoc, name: e.target.value })} className="rounded-lg md:col-span-2" data-testid="new-loc-name" />
          <Input placeholder="Nickname (optional)" value={newLoc.nickname || ''} onChange={e => setNewLoc({ ...newLoc, nickname: e.target.value })} className="rounded-lg md:col-span-1" data-testid="new-loc-nickname" title="Short label shown in the client dropdown — e.g. 'ABC — Katy'" />
          <Input placeholder="Address" value={newLoc.address} onChange={e => setNewLoc({ ...newLoc, address: e.target.value })} className="rounded-lg md:col-span-2" data-testid="new-loc-address" />
          <Select value={newLoc.type} onValueChange={v => setNewLoc({ ...newLoc, type: v })}>
            <SelectTrigger className="rounded-lg" data-testid="new-loc-type"><SelectValue /></SelectTrigger>
            <SelectContent>{LOCATION_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={newLoc.accepting_status || 'Yes'} onValueChange={v => setNewLoc({ ...newLoc, accepting_status: v })}>
            <SelectTrigger className={`rounded-lg font-semibold ${acceptingClass(newLoc.accepting_status)}`} data-testid="new-loc-accepting"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Yes">Yes</SelectItem>
              <SelectItem value="No">No</SelectItem>
              <SelectItem value="Check with Office Dir.">Check with Office Dir.</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={addLocation} className="bg-[#685B72] hover:bg-[#52475A] text-white rounded-lg" data-testid="add-loc-btn"><Plus size={14} className="mr-1" />Add</Button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#FAF8FB] text-[#685B72] text-left">
              <th className="px-3 py-2 font-semibold">Name</th>
              <th className="px-3 py-2 font-semibold" title="Short label shown in the client Eval/Therapy dropdown when set — falls back to Name if blank.">Nickname</th>
              <th className="px-3 py-2 font-semibold">Address</th>
              <th className="px-3 py-2 font-semibold">Type</th>
              <th className="px-3 py-2 font-semibold">Accepting</th>
              <th className="px-3 py-2 font-semibold">Notes</th>
              {canEdit && <th className="px-3 py-2 font-semibold"></th>}
            </tr>
          </thead>
          <tbody>
            {(clinic.outside_locations || []).map((loc, i) => {
              const acc = accepting(loc);
              return (
                <tr key={`${loc.name}-${i}`} className="border-t border-[#E8E5EC]" data-testid={`loc-row-${i}`}>
                  <td className="px-3 py-2 align-top">
                    {canEdit ? (
                      <InlineTextInput
                        value={loc.name || ''}
                        onCommit={v => updateLocation(i, { name: v })}
                        className="rounded-lg h-8 text-xs w-52"
                        data-testid={`loc-name-${i}`}
                      />
                    ) : (
                      <span className="text-[#2D2833] font-medium">{loc.name}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {canEdit ? (
                      <InlineTextInput
                        value={loc.nickname || ''}
                        onCommit={v => updateLocation(i, { nickname: v })}
                        className="rounded-lg h-8 text-xs w-40"
                        data-testid={`loc-nickname-${i}`}
                        placeholder="—"
                      />
                    ) : (
                      <span className="text-[#685B72] text-xs italic">{loc.nickname || '—'}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {canEdit ? (
                      <InlineTextInput
                        value={loc.address || ''}
                        onCommit={v => updateLocation(i, { address: v })}
                        className="rounded-lg h-8 text-xs w-64"
                        data-testid={`loc-address-${i}`}
                      />
                    ) : (
                      <span className="text-[#685B72]">{loc.address}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {canEdit ? (
                      <Select value={loc.type || 'Other'} onValueChange={v => updateLocation(i, { type: v })}>
                        <SelectTrigger className="rounded-lg h-8 min-w-[130px] text-xs" data-testid={`loc-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{LOCATION_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <span className="stage-badge bg-[#F1EDF5] text-[#52475A]">{loc.type}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {canEdit ? (
                      <Select value={acc} onValueChange={v => updateLocation(i, { accepting_status: v })}>
                        <SelectTrigger className={`rounded-lg h-8 min-w-[160px] font-semibold text-xs ${acceptingClass(acc)}`} data-testid={`loc-accepting-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                          <SelectItem value="Check with Office Dir.">Check with Office Dir.</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      acc === 'Yes' ? <span className="stage-badge bg-[#839E97]/20 text-[#839E97]">Yes</span>
                      : acc === 'No' ? <span className="stage-badge bg-[#8A3A3A]/15 text-[#8A3A3A]">No</span>
                      : <span className="stage-badge bg-[#B08A3A]/20 text-[#B08A3A]">Check with Office Dir.</span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {canEdit ? (
                      <InlineTextInput
                        value={loc.notes || ''}
                        onCommit={v => updateLocation(i, { notes: v })}
                        className="rounded-lg h-8 text-xs w-56"
                        data-testid={`loc-notes-${i}`}
                        placeholder="—"
                      />
                    ) : (
                      <span className="text-[#685B72] max-w-xs truncate">{loc.notes}</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-3 py-2 text-right align-top">
                      <button onClick={() => removeLocation(i)} className="text-[#685B72]/60 hover:text-[#8A3A3A]" data-testid={`del-loc-${i}`}><Trash2 size={14} /></button>
                    </td>
                  )}
                </tr>
              );
            })}
            {(!clinic.outside_locations || clinic.outside_locations.length === 0) && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-[#685B72]">No outside locations yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
